import React from "react";
import { motion } from "framer-motion";
import { Leaf } from "lucide-react";
import {
  STORY_DATA,
  PROCESS_STEPS,
  PHILOSOPHY_SECTIONS,
  IMAGES,
} from "../constants/kalorenceData";
import { Reveal } from "../components/Reveal";

export const Story = () => {
  return (
    <div data-testid="story-page" className="bg-[#F7F5F0]">
      {/* Editorial Hero */}
      <section className="relative h-[70vh] min-h-[480px] flex items-end overflow-hidden">
        <img
          src={IMAGES.img_2}
          alt="Botanicals laid on fabric"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2B3A2F]/90 via-[#2B3A2F]/30 to-[#2B3A2F]/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="text-[#F7F5F0] max-w-3xl"
          >
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#E2DACF] mb-4 flex items-center gap-2">
              <Leaf className="w-4 h-4 text-[#C47D65]" /> {STORY_DATA.label}
            </p>
            <h1 className="font-cormorant text-5xl sm:text-7xl font-light leading-[0.95]">
              {STORY_DATA.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Narrative */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <Reveal>
            <p className="text-lg sm:text-xl text-[#2B3A2F] font-light leading-relaxed first-letter:font-cormorant first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8] first-letter:text-[#C47D65]">
              {STORY_DATA.text1}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <blockquote className="font-cormorant italic text-3xl sm:text-4xl text-[#2B3A2F] font-light leading-snug text-center py-8 border-y border-[#E2DACF]">
              “{STORY_DATA.quote}”
            </blockquote>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base text-[#5C6B5F] font-light leading-relaxed">
              {STORY_DATA.text2}
            </p>
          </Reveal>
        </div>
      </section>

      {/* The Craft — Process steps, alternating */}
      <section className="bg-[#EDEAE2] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C47D65]">
                The Process
              </p>
              <h2 className="font-cormorant text-4xl sm:text-5xl font-light mt-3">
                From leaf to lasting impression
              </h2>
            </div>
          </Reveal>

          <div className="space-y-20 sm:space-y-28">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal key={step.id} testId={`process-step-${step.num}`}>
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                    i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div className="relative overflow-hidden group">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full aspect-[5/4] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="space-y-4">
                    <span className="font-cormorant text-6xl sm:text-7xl text-[#C47D65]/40 font-light leading-none">
                      {step.num}
                    </span>
                    <h3 className="font-cormorant text-3xl font-normal">{step.title}</h3>
                    <p className="text-base text-[#5C6B5F] font-light leading-relaxed max-w-md">
                      {step.description}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 sm:py-28 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C47D65]">
                Our Philosophy
              </p>
              <h2 className="font-cormorant text-4xl sm:text-5xl font-light mt-3 leading-tight">
                {PHILOSOPHY_SECTIONS.title}
              </h2>
              <p className="text-base text-[#5C6B5F] font-light leading-relaxed mt-5">
                {PHILOSOPHY_SECTIONS.subtitle}
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {PHILOSOPHY_SECTIONS.items.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.12}>
                <div className="space-y-3 border-t border-[#2B3A2F] pt-6">
                  <span className="font-cormorant text-3xl text-[#C47D65]/50">
                    0{i + 1}
                  </span>
                  <h3 className="font-cormorant text-2xl font-normal">{item.title}</h3>
                  <p className="text-sm text-[#5C6B5F] font-light leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
